import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQueryClient } from '@tanstack/react-query';
import {
  X, Loader2, Store, FileText, CheckCircle2, User, CreditCard, Calendar, AlertCircle,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

// Admin-triggered import of Square POS sales. Fetches recent in-person
// payments that don't yet have a matching website Order, lets the admin
// review each, fill in missing customer details (or allocate to the catch-all
// POS consumer account), then creates a paid Order + generates an invoice
// PDF — one row at a time so every sale is reviewed before it's saved.

const fmt = (n) => `£${(Number(n) || 0).toFixed(2)}`;

// Catch-all consumer account for nameless walk-in POS sales. Uses an internal
// email so these sales never trigger marketing / sign-up invitations.
const POS_ACCOUNT_DEFAULTS = {
  customer_name: 'POS Walk-in Sale',
  customer_email: 'possales@motionreality.co.uk',
  customer_phone: '',
};

export default function ImportPosSalesModal({ onClose }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [daysBack, setDaysBack] = useState(30);
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [scanned, setScanned] = useState(0);
  const [drafts, setDrafts] = useState({}); // { payment_id: { customer_name, customer_email, customer_phone, product_name } }
  const [creating, setCreating] = useState(null); // payment_id being created
  const [done, setDone] = useState(new Set()); // payment_ids already imported this session

  const fetchPos = async () => {
    setLoading(true);
    try {
      const res = await base44.functions.invoke('importSquarePosSales', { days_back: daysBack });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Fetch failed');
      setCandidates(res.data.candidates || []);
      setScanned(res.data.scanned || 0);
      // Pre-seed editable drafts with whatever Square gave us.
      const d = {};
      for (const c of res.data.candidates || []) {
        d[c.payment_id] = {
          customer_name: c.customer_name || '',
          customer_email: c.customer_email || '',
          customer_phone: '',
          product_name: c.product_name || 'POS Sale',
        };
      }
      setDrafts(d);
      setDone(new Set());
    } catch (e) {
      toast({ title: 'Could not fetch POS sales', description: e.message, variant: 'destructive' });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateDraft = (pid, field, value) => {
    setDrafts((prev) => ({ ...prev, [pid]: { ...prev[pid], [field]: value } }));
  };

  const allocateToPosAccount = (pid) => {
    setDrafts((prev) => ({
      ...prev,
      [pid]: {
        ...prev[pid],
        customer_name: POS_ACCOUNT_DEFAULTS.customer_name,
        customer_email: POS_ACCOUNT_DEFAULTS.customer_email,
      },
    }));
  };

  const createOrder = async (c) => {
    const draft = drafts[c.payment_id];
    if (!draft?.customer_email) {
      toast({ title: 'Customer email required', description: 'Enter an email or allocate to the POS account', variant: 'destructive' });
      return;
    }
    setCreating(c.payment_id);
    try {
      // Auto-link to an existing consumer account (email → Sage ref → name+postcode),
      // mirroring the legacy + Sage line-item import auto-linking.
      let consumer_account_id = '';
      let consumer_account_name = '';
      try {
        const m = await base44.functions.invoke('matchConsumerAccount', { email: draft.customer_email, name: draft.customer_name });
        if (m.data?.consumer_account_id) {
          consumer_account_id = m.data.consumer_account_id;
          consumer_account_name = m.data.consumer_account_name || '';
        }
      } catch (_e) { /* match is best-effort; order still created unlinked */ }
      const order = await base44.entities.Order.create({
        customer_name: draft.customer_name || 'POS Walk-in Sale',
        customer_email: draft.customer_email,
        customer_phone: draft.customer_phone || '',
        product_id: '',
        product_name: draft.product_name || 'POS Sale',
        amount: c.amount,
        status: 'paid',
        source: 'Square POS',
        square_payment_id: c.payment_id,
        square_order_id: c.order_id || '',
        payment_method: 'Card / Square POS',
        consumer_account_id,
        notes: `Imported POS sale · Square payment ${c.payment_id}`,
      });
      // Generate the branded invoice PDF (no email to customer).
      try {
        await base44.functions.invoke('generateOrderInvoice', { order_id: order.id });
      } catch (e) {
        console.warn('[ImportPos] invoice generation failed', e);
        toast({ title: 'Order created, invoice failed', description: e.message, variant: 'destructive' });
      }
      setDone((prev) => new Set(prev).add(c.payment_id));
      queryClient.invalidateQueries({ queryKey: ['admin-orders', 'all'] });
      toast({ title: 'POS sale imported', description: `${draft.product_name} · ${fmt(c.amount)}${consumer_account_name ? ` · linked to ${consumer_account_name}` : ''}` });
    } catch (e) {
      toast({ title: 'Create failed', description: e.message, variant: 'destructive' });
    }
    setCreating(null);
  };

  const remaining = candidates.filter((c) => !done.has(c.payment_id));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-8 overflow-y-auto">
      <div className="w-full max-w-4xl bg-[#111115] border border-white/10 my-auto">
        {/* Header */}
        <div className="flex items-center gap-3 p-5 border-b border-white/10">
          <Store className="w-5 h-5 text-primary" />
          <div className="flex-1">
            <p className="text-primary text-[10px] font-bold tracking-widest">SQUARE POS</p>
            <h2 className="text-white font-black text-lg uppercase">Import POS Sales</h2>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 p-4 border-b border-white/5 flex-wrap">
          <label className="text-white/40 text-[10px] font-bold tracking-widest">LOOKBACK</label>
          <select
            value={daysBack}
            onChange={(e) => setDaysBack(Number(e.target.value))}
            className="bg-[#0d0d0f] border border-white/10 text-white text-xs px-2 py-1.5 focus:border-primary/60"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
          <button
            onClick={fetchPos}
            disabled={loading}
            className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/30 text-white/60 hover:text-white text-[10px] font-bold tracking-widest px-3 py-1.5 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} REFRESH
          </button>
          <span className="text-white/30 text-[10px] ml-auto">
            {loading ? 'Scanning…' : `${remaining.length} to review · ${scanned} payments scanned`}
          </span>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : remaining.length === 0 ? (
          <div className="text-center py-16 text-white/30">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-bold tracking-widest">NO NEW POS SALES</p>
            <p className="text-xs mt-1">
              {candidates.length === 0
                ? 'Every recent Square payment is already tracked.'
                : 'All fetched POS sales have been imported.'}
            </p>
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-white/5">
            {remaining.map((c) => {
              const draft = drafts[c.payment_id] || {};
              return (
                <div key={c.payment_id} className="p-4 space-y-3">
                  {/* Payment summary */}
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex items-center gap-1.5 text-orange-400">
                      <CreditCard className="w-3.5 h-3.5" />
                      <span className="font-mono text-[11px] font-bold">{c.payment_id.slice(-12)}</span>
                    </div>
                    <span className="text-white/40 text-[11px] inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(c.created_at).toLocaleString('en-GB')}
                    </span>
                    {c.entry_method && (
                      <span className="text-[9px] font-bold tracking-widest border border-white/15 text-white/50 px-1.5 py-0.5 uppercase">
                        {c.source_type} · {c.entry_method}
                      </span>
                    )}
                    <span className="text-white font-black text-lg ml-auto">{fmt(c.amount)}</span>
                  </div>

                  {/* Line items from Square (read-only) */}
                  {c.line_items?.length > 0 && (
                    <div className="text-white/40 text-[11px] pl-6">
                      Items: {c.line_items.map((li) => `${li.quantity}× ${li.name}${li.variation_name ? ' (' + li.variation_name + ')' : ''}`).join(', ')}
                    </div>
                  )}

                  {/* Editable draft fields */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-6">
                    <input
                      value={draft.customer_name || ''}
                      onChange={(e) => updateDraft(c.payment_id, 'customer_name', e.target.value)}
                      placeholder="Customer name"
                      className="seo-input"
                    />
                    <input
                      value={draft.customer_email || ''}
                      onChange={(e) => updateDraft(c.payment_id, 'customer_email', e.target.value)}
                      placeholder="Customer email"
                      className="seo-input"
                    />
                    <input
                      value={draft.customer_phone || ''}
                      onChange={(e) => updateDraft(c.payment_id, 'customer_phone', e.target.value)}
                      placeholder="Phone (optional)"
                      className="seo-input"
                    />
                    <input
                      value={draft.product_name || ''}
                      onChange={(e) => updateDraft(c.payment_id, 'product_name', e.target.value)}
                      placeholder="Product / description"
                      className="seo-input"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pl-6 flex-wrap">
                    <button
                      onClick={() => allocateToPosAccount(c.payment_id)}
                      className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/30 text-white/60 hover:text-white text-[10px] font-bold tracking-widest px-3 py-1.5 transition-colors"
                    >
                      <User className="w-3 h-3" /> USE POS ACCOUNT
                    </button>
                    {!draft.customer_email && (
                      <span className="inline-flex items-center gap-1 text-yellow-400/70 text-[10px]">
                        <AlertCircle className="w-3 h-3" /> Email required
                      </span>
                    )}
                    <button
                      onClick={() => createOrder(c)}
                      disabled={creating === c.payment_id}
                      className="ml-auto inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold text-[10px] tracking-widest px-4 py-1.5 transition-colors"
                    >
                      {creating === c.payment_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                      CREATE ORDER + INVOICE
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t border-white/5 text-white/30 text-[10px]">
          Review each sale, enter the customer's details if you know them (or use the POS account for walk-ins),
          then create the order. An invoice PDF is generated automatically — no customer email is sent.
        </div>
      </div>
    </div>
  );
}